import React from "react";
import {
  MessageCircle,
  CheckCircle2,
  FileSearch,
  Target,
  ClipboardList,
  SendHorizontal,
  FileCheck2,
  CalendarCheck,
  Phone,
  Mail,
  Globe,
  Clock,
} from "lucide-react";

// ---- shared ----
const WHATSAPP_NUMBER = "61407720610";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Hello Your Hope, I would like to enquire about the professional readiness assessment."
);
const WA_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
const INTAKE_FORM_URL =
  "https://prettyform.addxt.com/a/form/vf/1FAIpQLSdV_Et3xHKOSOkdrfdydkfYSYN7GB_30lsq7b6QBky9cwMlFw";
const PRIVACY_NOTICE_URL =
  "https://docs.google.com/document/d/1vf_Av28qCOqGyCM2DYNaiiRe_SqegpmD/edit?usp=sharing&ouid=107596587615894985343&rtpof=true&sd=true";

// Warm editorial palette
const CREAM = "#F7EFDF";       // page / section background
const CREAM_SOFT = "#FBF6EA";  // raised card background
const NAVY = "#0B2447";
const GOLD = "#C9A227";
const SAGE = "#8A9B7A";

const WhatsAppButton = ({ full = false, variant = "primary", label, testid }) => {
  const base =
    "inline-flex items-center justify-center gap-2 font-semibold text-[15px] px-6 py-4 rounded-sm transition-colors";
  const styles =
    variant === "primary"
      ? "bg-[#C9A227] hover:bg-[#D4A93A] text-[#0B2447] shadow-md"
      : "bg-[#0B2447] hover:bg-[#11254A] text-white";
  const width = full ? "w-full sm:w-auto" : "";
  return (
    <a
      href={INTAKE_FORM_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles} ${width}`}
      data-testid={testid}
    >
      <MessageCircle size={18} />
      {label || "Request your assessment"}
    </a>
  );
};

// ========== HEADER (compact) ==========
export const HeaderLaunchV2 = () => (
  <header
    className="w-full border-b border-[#0B2447]/10 sticky top-0 z-50 backdrop-blur-sm"
    style={{ background: "rgba(247,239,223,0.92)" }}
    data-testid="header-launch"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4">
      <a href="#top" className="flex items-center gap-3 min-w-0">
        <img
          src="/assets/yourhope-logo.png?v=3"
          alt="Your Hope logo"
          className="h-11 sm:h-12 w-auto shrink-0"
          style={{ objectFit: "contain" }}
        />
        <div className="leading-tight min-w-0">
          <h1 className="font-display text-xl sm:text-2xl tracking-tight text-[#0B2447] font-bold truncate">
            YOUR HOPE
          </h1>
          <p className="font-body text-[9.5px] sm:text-[10.5px] tracking-[0.22em] text-[#0B2447]/60 font-semibold truncate">
            DOCUMENTATION &amp; EDUCATION SERVICES
          </p>
        </div>
      </a>
      <a
        href={WA_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden sm:inline-flex items-center gap-2 text-[13px] font-semibold text-[#0B2447] hover:text-[#C9A227] transition-colors"
        data-testid="header-whatsapp"
      >
        <MessageCircle size={15} className="text-[#25D366]" />
        WhatsApp us
      </a>
    </div>
  </header>
);

// ---- Botanical decoration (muted sage leaves) ----
const BotanicalDecor = () => (
  <svg
    aria-hidden
    viewBox="0 0 400 420"
    className="absolute right-[-60px] top-[-30px] w-[260px] sm:w-[340px] lg:w-[440px] opacity-70 pointer-events-none"
  >
    <circle cx="260" cy="200" r="200" fill="#EADFC5" />
    <path
      d="M240 420 C 260 320 230 220 260 120 C 275 70 250 20 280 10"
      stroke={SAGE}
      strokeWidth="2"
      fill="none"
      opacity="0.85"
    />
    {[
      { cx: 215, cy: 320, rx: 42, ry: 16, rot: -22 },
      { cx: 300, cy: 300, rx: 46, ry: 17, rot: 24 },
      { cx: 210, cy: 240, rx: 40, ry: 15, rot: -28 },
      { cx: 300, cy: 215, rx: 44, ry: 16, rot: 20 },
      { cx: 225, cy: 160, rx: 36, ry: 14, rot: -24 },
      { cx: 290, cy: 130, rx: 38, ry: 15, rot: 22 },
      { cx: 260, cy: 70, rx: 30, ry: 12, rot: 0 },
    ].map((l, i) => (
      <ellipse
        key={i}
        cx={l.cx}
        cy={l.cy}
        rx={l.rx}
        ry={l.ry}
        fill={SAGE}
        opacity={0.72}
        transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}
      />
    ))}
  </svg>
);

// ========== HERO ==========
export const HeroLaunchV2 = () => (
  <section
    id="top"
    className="relative overflow-hidden border-b border-[#0B2447]/10"
    style={{ background: CREAM }}
    data-testid="section-hero"
  >
    <BotanicalDecor />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
          STUDY · PLANNING · APPLICATION READINESS
        </p>
        <h2 className="font-display text-[2.1rem] sm:text-5xl lg:text-[3.4rem] leading-[1.05] font-bold text-[#0B2447] mt-4">
          Get clarity{" "}
          <span className="italic-serif text-[#C9A227]">before</span> committing.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#0B2447]/80 leading-relaxed max-w-xl">
          Understand your study profile, funding and evidence gaps, and the next
          steps to prioritise.
        </p>
        <p className="mt-3 italic-serif text-[15px] sm:text-base text-[#0B2447]/75 max-w-xl">
          Receive a personalised written assessment, delivered to your phone.
        </p>

        <div
          className="mt-7 inline-flex items-center gap-3 rounded-sm border px-4 py-2"
          style={{ background: CREAM_SOFT, borderColor: "rgba(201,162,39,0.45)" }}
        >
          <span className="text-[11px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
            PILOT PRICE
          </span>
          <span className="font-display text-2xl sm:text-3xl font-bold text-[#0B2447]">
            US$60
          </span>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <WhatsAppButton full testid="hero-cta" />
        </div>
        <p className="mt-3 text-[13px] text-[#0B2447]/70 max-w-xl">
          Complete a short intake form. Submitting it does not commit you to
          payment. We review your request and confirm the next steps first.
        </p>
      </div>

      <div className="lg:col-span-5">
        <SampleReportPreview />
      </div>
    </div>
  </section>
);

// ---- Illustrative sample preview card ----
const SampleReportPreview = () => (
  <figure
    className="rounded-md border overflow-hidden"
    style={{
      background: CREAM_SOFT,
      borderColor: "rgba(11,36,71,0.1)",
      boxShadow: "0 20px 40px -20px rgba(11,36,71,0.25)",
    }}
    data-testid="sample-report-preview"
  >
    <div className="bg-[#0B2447] text-white px-5 py-3 flex items-center justify-between">
      <div>
        <p className="text-[10px] tracking-[0.26em] font-semibold text-[#C9A227]">
          YOUR HOPE
        </p>
        <p className="font-display text-[15px] font-semibold leading-tight">
          Readiness Assessment
        </p>
      </div>
      <span className="text-[10px] tracking-[0.22em] font-semibold text-white/70">
        v1.0
      </span>
    </div>
    <div className="p-5 space-y-4">
      <div>
        <p className="text-[10px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
          APPLICANT
        </p>
        <p className="font-display text-[15px] font-semibold text-[#0B2447]">
          Sample Applicant · Postgraduate planning
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-sm border border-[#0F4D2C]/20 bg-[#0F4D2C]/5 p-3">
          <p className="text-[10px] tracking-[0.22em] font-semibold text-[#0F4D2C]">
            PROFILE FINDINGS
          </p>
          <p className="text-[12.5px] text-[#0B2447]/85 mt-1 leading-snug">
            Strong academic record · clear study intent · experience aligned with pathway.
          </p>
        </div>
        <div className="rounded-sm border border-[#C9A227]/40 bg-[#C9A227]/10 p-3">
          <p className="text-[10px] tracking-[0.22em] font-semibold text-[#C9A227]">
            GAPS IDENTIFIED
          </p>
          <p className="text-[12.5px] text-[#0B2447]/85 mt-1 leading-snug">
            Funding plan under target · sponsor evidence incomplete · one qualification unverified.
          </p>
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
          PRIORITY ACTIONS
        </p>
        <ul className="mt-2 space-y-1.5 text-[12.5px] text-[#0B2447]/85">
          <li className="flex gap-2">
            <span className="font-display font-bold text-[#C9A227]">01</span>
            Request a certified transcript for the missing qualification.
          </li>
          <li className="flex gap-2">
            <span className="font-display font-bold text-[#C9A227]">02</span>
            Prepare source-of-funds summary with sponsor documents.
          </li>
          <li className="flex gap-2">
            <span className="font-display font-bold text-[#C9A227]">03</span>
            Confirm scholarship eligibility windows at two shortlisted institutions.
          </li>
        </ul>
      </div>
    </div>
    <figcaption
      className="px-5 py-3 border-t border-[#0B2447]/10 text-[11px] italic-serif text-[#0B2447]/65"
      style={{ background: CREAM }}
    >
      Illustrative sample — fictional applicant.
    </figcaption>
  </figure>
);

// ========== WHAT YOU RECEIVE ==========
const RECEIVE_ITEMS = [
  {
    icon: FileSearch,
    title: "Profile findings",
    body: "A review of your education, experience and study goals.",
  },
  {
    icon: ClipboardList,
    title: "Funding and evidence gaps",
    body: "Identify budget shortfalls, missing documents and inconsistencies.",
  },
  {
    icon: Target,
    title: "Priority action plan",
    body: "Practical next steps, arranged in order of importance.",
  },
];

export const WhatYouReceiveV2 = () => (
  <section
    id="what-you-receive"
    style={{ background: CREAM }}
    data-testid="section-what-you-receive"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-center">
        <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
          WHAT YOU RECEIVE
        </p>
        <h3 className="font-display text-3xl sm:text-4xl text-[#0B2447] font-semibold leading-tight mt-3">
          Your personalised written readiness assessment
        </h3>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {RECEIVE_ITEMS.map((it, i) => (
          <div
            key={i}
            className="rounded-md border p-6"
            style={{
              background: CREAM_SOFT,
              borderColor: "rgba(11,36,71,0.1)",
            }}
            data-testid={`receive-item-${i}`}
          >
            <span className="w-11 h-11 rounded-sm bg-[#C9A227]/20 text-[#C9A227] flex items-center justify-center">
              <it.icon size={22} strokeWidth={1.6} />
            </span>
            <h4 className="font-display text-xl text-[#0B2447] font-semibold mt-4 leading-tight">
              {it.title}
            </h4>
            <p className="text-[15px] text-[#0B2447]/80 mt-2 leading-relaxed">
              {it.body}
            </p>
          </div>
        ))}
      </div>

      <p className="mt-10 text-center max-w-3xl mx-auto text-[14px] italic-serif text-[#0B2447]/70">
        Your Hope supports study planning and application readiness. Additional
        documentation preparation and organisation are available by agreement.
      </p>
    </div>
  </section>
);

// ========== HOW IT WORKS ==========
const STEPS = [
  {
    icon: MessageCircle,
    title: "Tell us your goals",
    body: "Complete our intake form with your study plans and the support you need.",
  },
  {
    icon: FileCheck2,
    title: "Confirm your service",
    body:
      "We confirm the scope, fee and timing. You accept the service agreement and arrange payment.",
  },
  {
    icon: SendHorizontal,
    title: "Send requested documents",
    body:
      "Receive a tailored checklist and provide the information and documents requested.",
  },
  {
    icon: CalendarCheck,
    title: "Receive your assessment",
    body:
      "Get your personalised written report with findings, gaps and priority actions.",
  },
];

export const HowItWorksLaunchV2 = () => (
  <section
    id="how-it-works"
    className="border-y border-[#0B2447]/10"
    style={{ background: CREAM_SOFT }}
    data-testid="section-how-it-works"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-center">
        <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
          HOW IT WORKS
        </p>
        <h3 className="font-display text-3xl sm:text-4xl text-[#0B2447] font-semibold leading-tight mt-3">
          Four simple steps
        </h3>
      </div>

      <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <li
            key={i}
            className="rounded-md border p-6 relative"
            style={{ background: CREAM, borderColor: "rgba(11,36,71,0.1)" }}
            data-testid={`step-${i + 1}`}
          >
            <span className="absolute top-4 right-4 font-display text-[32px] font-bold text-[#C9A227]/40 leading-none">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="w-11 h-11 rounded-full bg-[#0B2447] text-[#C9A227] flex items-center justify-center">
              <s.icon size={20} strokeWidth={1.7} />
            </span>
            <h4 className="font-display text-lg text-[#0B2447] font-semibold mt-4 leading-tight pr-10">
              {s.title}
            </h4>
            <p className="text-[14.5px] text-[#0B2447]/80 mt-2 leading-relaxed">
              {s.body}
            </p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

// ========== PRICING ==========
export const PricingLaunchV2 = () => (
  <section
    id="pricing"
    style={{ background: CREAM }}
    data-testid="section-pricing"
  >
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-center">
        <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
          PRICE &amp; TURNAROUND
        </p>
        <h3 className="font-display text-3xl sm:text-4xl text-[#0B2447] font-semibold leading-tight mt-3">
          Professional Application Readiness Assessment
        </h3>
      </div>

      <div
        className="mt-8 rounded-md border overflow-hidden shadow-sm"
        style={{
          background: CREAM_SOFT,
          borderColor: "rgba(201,162,39,0.4)",
        }}
      >
        <div className="bg-[#0B2447] text-white px-6 py-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <p className="text-[11px] tracking-[0.26em] font-semibold text-[#C9A227]">
              PILOT PRICE
            </p>
            <p className="font-display text-4xl sm:text-5xl font-bold mt-1">
              US$60
            </p>
          </div>
          <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-white/85">
            <Clock size={15} className="text-[#C9A227]" />
            48-hour turnaround
          </span>
        </div>

        <div className="p-6 sm:p-8 space-y-5">
          <div>
            <p className="text-[11px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
              INCLUDED
            </p>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6">
              {[
                "Profile and affordability review",
                "Funding and evidence gaps",
                "Priority actions",
                "Personalised written report delivered to your phone",
              ].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-[15px] text-[#0B2447]/90"
                  data-testid={`pricing-included-${i}`}
                >
                  <CheckCircle2
                    size={16}
                    className="text-[#0F4D2C] mt-1 shrink-0"
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="rounded-sm border p-4"
            style={{ background: CREAM, borderColor: "rgba(11,36,71,0.1)" }}
          >
            <p className="text-[13.5px] text-[#0B2447]/85 leading-relaxed">
              <span className="font-semibold">48 hours</span> after we confirm
              receipt of complete information, required documents and payment.
            </p>
            <p className="text-[13.5px] text-[#0B2447]/85 leading-relaxed mt-2">
              <span className="font-semibold">Consultation optional.</span> Not
              included in US$60.
            </p>
            <p className="text-[13.5px] text-[#0B2447]/85 leading-relaxed mt-2">
              Additional documentation support can be discussed and quoted
              separately after review.
            </p>
          </div>

          <div className="pt-2">
            <WhatsAppButton full testid="pricing-cta" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ========== FAQ ==========
const FAQS = [
  {
    q: "What does the assessment cover?",
    a:
      "It reviews the information and documents you provide to identify profile findings, funding and evidence gaps, and practical next steps. We confirm the agreed scope before payment.",
  },
  {
    q: "When does the 48-hour turnaround start?",
    a:
      "48 hours after we confirm receipt of complete information, required documents and payment.",
  },
  {
    q: "Does this include preparing or submitting my application?",
    a:
      "Application preparation and submission are not included in the assessment. Any additional documentation support is agreed and quoted separately.",
  },
  {
    q: "Does the assessment guarantee admission, funding or a visa?",
    a:
      "No. It supports planning and preparation. Institutions, funders and relevant authorities make their own decisions.",
  },
];

export const FaqLaunchV2 = () => (
  <section
    id="faq"
    className="border-y border-[#0B2447]/10"
    style={{ background: CREAM_SOFT }}
    data-testid="section-faq"
  >
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-center">
        <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
          FAQ
        </p>
        <h3 className="font-display text-3xl sm:text-4xl text-[#0B2447] font-semibold leading-tight mt-3">
          A few things to know
        </h3>
      </div>

      <div className="mt-8 space-y-3">
        {FAQS.map((f, i) => (
          <details
            key={i}
            className="group rounded-md border border-[#0B2447]/10 open:shadow-sm transition-colors"
            style={{ background: CREAM }}
            data-testid={`faq-item-${i}`}
          >
            <summary className="list-none cursor-pointer px-5 py-4 flex items-center justify-between gap-4">
              <span className="font-display text-[16px] sm:text-[17px] text-[#0B2447] font-semibold leading-snug">
                {f.q}
              </span>
              <span className="w-7 h-7 rounded-full bg-[#C9A227]/15 text-[#C9A227] flex items-center justify-center transition-transform group-open:rotate-45">
                <span className="text-xl leading-none font-bold">+</span>
              </span>
            </summary>
            <p className="px-5 pb-5 text-[15px] text-[#0B2447]/80 leading-relaxed">
              {f.a}
            </p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

// ========== FINAL CTA ==========
export const FinalCtaV2 = () => (
  <section
    id="final-cta"
    className="bg-[#0B2447] text-white"
    data-testid="section-final-cta"
  >
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20 text-center">
      <p className="text-[11px] tracking-[0.28em] font-semibold text-[#C9A227]">
        READY TO START?
      </p>
      <h3 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mt-3">
        Request your assessment
      </h3>
      <p className="mt-4 text-[15px] text-white/80 leading-relaxed max-w-2xl mx-auto">
        Complete our intake form to tell us about your study plans. Submitting
        it does not commit you to payment. We review your request and confirm
        the service scope, required documents and payment instructions before
        the assessment begins.
      </p>
      <div className="mt-7 flex justify-center">
        <WhatsAppButton full testid="final-cta" />
      </div>
    </div>
  </section>
);

// ========== FOOTER ==========
export const FooterLaunchV2 = () => (
  <footer
    className="border-t border-[#0B2447]/10"
    style={{ background: CREAM }}
    data-testid="section-footer"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="flex items-start gap-3">
        <img
          src="/assets/yourhope-logo.png?v=3"
          alt="Your Hope logo"
          className="h-11 w-auto shrink-0"
          style={{ objectFit: "contain" }}
        />
        <div className="leading-tight">
          <p className="font-display text-xl text-[#0B2447] font-bold">YOUR HOPE</p>
          <p className="text-[10.5px] tracking-[0.22em] text-[#0B2447]/60 font-semibold">
            DOCUMENTATION &amp; EDUCATION SERVICES
          </p>
        </div>
      </div>

      <div>
        <p className="text-[11px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
          CONTACT
        </p>
        <ul className="mt-3 space-y-2 text-[14.5px] text-[#0B2447]/85">
          <li className="flex items-center gap-2">
            <MessageCircle size={14} className="text-[#25D366]" />
            <a
              href={WA_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#C9A227]"
              data-testid="footer-whatsapp"
            >
              Send us an enquiry on WhatsApp.
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Mail size={14} className="text-[#C9A227]" />
            <a href="mailto:info@yourhopedoc.com" className="hover:text-[#C9A227]">
              info@yourhopedoc.com
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Globe size={14} className="text-[#C9A227]" />
            <a
              href="https://yourhopedoc.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#C9A227]"
            >
              yourhopedoc.com
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Phone size={14} className="text-[#C9A227]" />
            <a href="tel:+61407720610" className="hover:text-[#C9A227]">
              +61 407 720 610
            </a>
          </li>
        </ul>
      </div>

      <div>
        <p className="text-[11px] tracking-[0.22em] font-semibold text-[#0B2447]/60">
          IMPORTANT
        </p>
        <p className="mt-3 text-[13px] text-[#0B2447]/75 leading-relaxed italic-serif">
          Your Hope provides readiness assessment and documentation support. We
          do not provide legal or migration advice. The assessment supports
          planning and is not an admission or visa decision.
        </p>
        <a
          href={PRIVACY_NOTICE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-[#0B2447] hover:text-[#C9A227] underline-offset-4 hover:underline"
          data-testid="footer-privacy-notice"
        >
          Privacy Notice
        </a>
      </div>
    </div>
    <div className="border-t border-[#0B2447]/10">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 text-[12px] text-[#0B2447]/60">
        © {new Date().getFullYear()} Your Hope — Documentation &amp; Education Services.
      </div>
    </div>
  </footer>
);

export default function LaunchLanding() {
  return (
    <div
      className="App min-h-screen"
      style={{ background: CREAM }}
      data-testid="landing-root"
    >
      <HeaderLaunchV2 />
      <HeroLaunchV2 />
      <WhatYouReceiveV2 />
      <HowItWorksLaunchV2 />
      <PricingLaunchV2 />
      <FaqLaunchV2 />
      <FinalCtaV2 />
      <FooterLaunchV2 />
    </div>
  );
}
